import { useLocation, Link } from "react-router-dom";
import { PAGES } from "../content/pages";
import { PAGES_HI } from "../content/pages.hi";
import { Seo } from "../components/Seo";
import { Breadcrumbs, Disclaimer } from "../components/Layout";
import { CtaLink, FaqList, Slot, useFill, usePathCrumbs } from "../components/Blocks";
import { useT } from "../lib/i18n";
import NotFound from "./NotFound";

const pick = (h, e) => (h === undefined ? e : h);

function localise(e, h, tl) {
  if (!h) {
    tl(e.h1);
    return e;
  }
  return {
    ...e, title: h.title, description: h.description, h1: h.h1, draft: pick(h.draft, e.draft),
    sections: e.sections?.map((s, i) => {
      const x = h.sections?.[i] || {};
      return { ...s, h2: pick(x.h2, s.h2), p: pick(x.p, s.p), list: pick(x.list, s.list), links: s.links?.map(([l, href], j) => [x.links?.[j] ?? l, href]) };
    }),
    links: e.links?.map((l, j) => ({ ...l, label: h.links?.[j] ?? tl(l.label) })),
    cta: e.cta && { ...e.cta, label: h.cta ?? tl(e.cta.label) },
    faq: h.faq ?? e.faq,
  };
}

function Section({ s, fill }) {
  return (
    <section className="mb-10">
      <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">{s.h2}</h2>
      {s.p?.map((t) => <p key={t} className="text-slate-700 leading-relaxed mb-4">{fill(t)}</p>)}
      {s.list && <ul className="space-y-2 mb-4">{s.list.map((t) => <li key={t} className="flex gap-3 text-slate-700"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-amber-600 shrink-0" />{fill(t)}</li>)}</ul>}
      {s.links && <ul className="space-y-2">{s.links.map(([l, href]) => <li key={href}><a className="text-amber-800 underline" href={href} target="_blank" rel="noopener noreferrer">{l}</a></li>)}</ul>}
      {s.slot && <Slot name={s.slot} />}
    </section>
  );
}

export default function ContentPage() {
  const { pathname } = useLocation();
  const { lang, tl } = useT();
  const path = pathname.replace(/\/+$/, "");
  const base = PAGES[path];
  const page = base && (lang === "hi" ? localise(base, PAGES_HI[path], tl) : base);
  const fill = useFill();
  const crumbs = usePathCrumbs(page ? pathname.split("/").filter(Boolean).map((_, i, a) => (i === a.length - 1 ? base.h1 : undefined)) : []);
  if (!page) return <NotFound />;
  return (
    <div className="container-x py-12 sm:py-16 max-w-4xl" data-testid="content-page">
      <Seo title={page.title} description={page.description} noindex={page.noindex} crumbs={crumbs} />
      <Breadcrumbs items={crumbs} />
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 mb-6 reveal" data-testid="page-h1">{page.h1}</h1>
      {page.draft && <div className="mb-8"><Disclaimer testid="draft-banner">{page.draft}</Disclaimer></div>}
      {page.sections?.map((s) => <Section key={s.h2} s={s} fill={fill} />)}
      {page.faq && <FaqList items={page.faq} />}
      {page.links && <div className="flex flex-wrap gap-4 mb-8">{page.links.map((l) => <Link key={l.to} to={l.to} className="text-amber-800 font-semibold underline">{l.label}</Link>)}</div>}
      {page.cta && <div className="mt-6"><CtaLink to={page.cta.to} testid="page-cta">{page.cta.label}</CtaLink></div>}
    </div>
  );
}
